"use client";

import { getEnv } from "@/lib/env";
import {
  PrivyProvider as BasePrivyProvider,
  usePrivy,
  useSessionSigners,
  useWallets,
} from "@privy-io/react-auth";
// import { useWallets as useSolanaWallets } from "@privy-io/react-auth/solana";
// import { Buffer as NodeBuffer } from "buffer";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState, createContext, useContext } from "react";
import { seiTestnet } from "viem/chains";
import { MonacoSDKProvider } from "@nitro-ape/shared/features/monaco/api/monaco-sdk-provider";

// Context to signal when Privy is ready
const PrivyReadyContext = createContext(false);
export const useIsPrivyReady = () => useContext(PrivyReadyContext);

// const globalForBuffer = globalThis as typeof globalThis & {
//   Buffer?: typeof NodeBuffer;
// };

// globalForBuffer.Buffer ??= NodeBuffer;

interface PrivyProviderProps {
  children: ReactNode;
}

/**
 * Component that provides Monaco SDK with Privy data.
 * Uses Privy hooks HERE (where they work) and passes data as props to MonacoSDKProvider.
 * This avoids the context availability issue where MonacoSDKProvider can't access Privy context.
 */
function PrivyReadyGate({ children }: { children: ReactNode }) {
  // These hooks work because this component is a direct child of BasePrivyProvider
  const { ready: privyReady, authenticated, user } = usePrivy();
  const { wallets, ready: walletsReady } = useWallets();

  // Always render MonacoSDKProvider, passing Privy data as props
  // This way MonacoSDKProvider doesn't need to call Privy hooks directly
  return (
    <PrivyReadyContext.Provider value={privyReady}>
      <MonacoSDKProvider
        privyReady={privyReady}
        authenticated={authenticated}
        user={user}
        wallets={wallets}
        walletsReady={walletsReady}
      >
        {children}
      </MonacoSDKProvider>
    </PrivyReadyContext.Provider>
  );
}

function SessionSignerManager() {
  const { wallets: rawWallets, ready: walletsReady } = useWallets();
  const { user, ready: privyReady, authenticated } = usePrivy();

  const filteredWallets = useMemo(
    () => rawWallets.filter((wallet) => wallet.walletClientType === "privy"),
    [rawWallets],
  );
  // const { wallets: solanaWallets, ready: solanaWalletsReady } =
  //   useSolanaWallets();
  const { addSessionSigners } = useSessionSigners();

  // const wallets = [...ethereumWallets, ...solanaWallets];

  useEffect(() => {
    const setupSessionSigners = async () => {
      const sessionSignerId = getEnv.VITE_PRIVY_SESSION_SIGNER_ID;

      if (
        !sessionSignerId ||
        !walletsReady ||
        // !solanaWalletsReady ||
        filteredWallets.length === 0 ||
        // solanaWallets.length === 0 ||
        !user
      ) {
        return;
      }

      // Get addresses of wallets that already have session signers (delegated)
      const delegatedWalletAddresses = new Set(
        user.linkedAccounts
          .filter(
            (
              account,
            ): account is Extract<
              typeof account,
              { type: "wallet"; address: string }
            > =>
              account.type === "wallet" &&
              "walletClientType" in account &&
              account.walletClientType === "privy" &&
              "delegated" in account &&
              account.delegated === true,
          )
          .map((account) => account.address.toLowerCase()),
      );

      // Filter for embedded wallets that don't already have session signers
      const embeddedWalletsWithoutSigners = filteredWallets.filter(
        (wallet) =>
          "walletClientType" in wallet &&
          wallet.walletClientType === "privy" &&
          !delegatedWalletAddresses.has(wallet.address.toLowerCase()),
      );

      if (embeddedWalletsWithoutSigners.length === 0) {
        return;
      }

      for (const embeddedWallet of embeddedWalletsWithoutSigners) {
        try {
          await addSessionSigners({
            address: embeddedWallet.address,
            signers: [
              {
                signerId: sessionSignerId,
                // Empty policyIds array means the session signer has full permissions
                policyIds: [],
              },
            ],
          });
        } catch (signerError) {
          console.error(signerError);
        }
      }
    };

    setupSessionSigners();
  }, [filteredWallets, walletsReady, user, addSessionSigners]);

  return null;
}

export function PrivyProvider({ children }: PrivyProviderProps) {
  // Track if we're on the client (avoids hydration mismatch)
  const [isClient, setIsClient] = useState(false);
  
  useEffect(() => {
    setIsClient(true);
  }, []);

  const appId = getEnv.VITE_PRIVY_APP_ID;

  // On server or before client hydration, render children without Privy
  if (!isClient || !appId) {
    return <>{children}</>;
  }

  // On client with valid appId, render with Privy and Monaco SDK providers
  return (
    <BasePrivyProvider
      appId={appId}
      config={{
        supportedChains: [seiTestnet],
        loginMethods: ["email", "google", "discord", "telegram", "wallet"],
        appearance: {
          theme: "dark",
          accentColor: "#676FFF",
        },
        embeddedWallets: {
          // `"all-users"` (not `"users-without-wallets"`) so Privy provisions
          // an embedded EVM + Solana wallet even when the user signs in with
          // an external wallet (e.g. Metamask). Monaco, the testnet faucet
          // panel, and the whitelist request all look up the embedded
          // wallet via `wallets.find(w => w.walletClientType === "privy")`
          // and silently no-op when it's missing — leaving wallet-login
          // users stuck on "Connecting to Monaco…". The external wallet
          // remains linked purely as the identity proof.
          ethereum: {
            createOnLogin: "all-users",
          },
          solana: {
            createOnLogin: "all-users",
          },
        },
      }}
    >
      <SessionSignerManager />
      <PrivyReadyGate>
        {children}
      </PrivyReadyGate>
    </BasePrivyProvider>
  );
}
